import React, { useState } from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Box,
  Collapse,
  Divider,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";

const HelpSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);

  const handleToggle = (): void => {
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const toggleExpand = (topic: string): void => {
    setExpandedTopic(expandedTopic === topic ? null : topic);
  };

  // Define Synonyms for each topic title
  const topicSynonyms = {
    "How to create a template?": ["create template", "template creation", "make template"],
    "How to send a template?": ["send template", "template sending", "send a message template"],
    "How to send Bulk template?": ["send bulk template", "bulk sending", "bulk template sending"],
    "What is the full process to create and send a template?": [
      "full process to create and send template",
      "steps to create and send template",
      "how to create and send template",
    ],
    "What are the different template categories, and when should you use each?": [
      "template categories",
      "categories of templates",
      "when to use each template category",
    ],
    "What are Body Variables in a Template?": [
      "body variables in template",
      "template body variables",
      "what are template variables",
    ],
    "How do body variables work?": [
      "how do template variables work",
      "working of body variables",
      "how body variables function",
    ],
    "Example of a body variable?": [
      "example of template body variable",
      "body variable example",
      "template variable example",
    ],
    "What are the different types of buttons available in a template?": [
      "types of buttons in template",
      "button types in template",
      "available buttons in template",
    ],
  };

  const helpTopics = [
    {
      title: "How to create a template?",
      steps: [
        "1. Click on Create Template",
        "2. Fill Out all mandatory fields",
        "3. Fill Out all mandatory fields",
        "4. Click on Submit",
        "5. Now your template created successfully"
      ],
      videoUrl: "https://mdimages.s3.amazonaws.com/1745655079729WhatsApp_Video_2025-04-25_at_11.36.35.mp4",
    },
    {
      title: "How to send a template?",
      steps: [
        "1. Click on three dot icon which in Action Column",
        "2. Click on Send from Dropdown",
        "3. Fill out Parameters",
        "4. Click on Send",
      ],
      videoUrl: "https://mdimages.s3.amazonaws.com/1745655109364WhatsApp_Video_2025-04-26_at_12.32.05.mp4",
    },
    {
      title: "How to send Bulk template?",
      steps: [
        "1. Click on three dot icon which in Action Column",
        "2. Click on Send from Dropdown",
        "3. Download Sample sheet",
        "4. Fill out Template sheet",
        "5. Upload the sheet",
      ],
      videoUrl: "",
    },
    {
      title: "What is the full process to create and send a template?",
      steps: [
        "Step 1: Open the Menu - Click the menu button in the top-left corner after login.",
        "Step 2: Navigate to Templates - From the menu, select 'Templates'.",
        "Step 3: View the Template Dashboard - The dashboard displays your existing templates.",
        "Step 4: Click Create Template - Use the 'Create Template' button to start building.",
        "Step 5: Fill Template Form (Category) - Choose Marketing, Utility, or Authentication category.",
        "Step 6: Add Name and Description - Write a name and (optionally) a description.",
        "Step 7: Select Language - Pick the language, like English.",
        "Step 8: Add Header - Select text, image, video, or document as your header.",
        "Step 9: Write the Body - Add dynamic text with variables like name or amount.",
        "Step 10: Add Footer (Optional) - Include a simple closing line or note.",
        "Step 11: Add Button (Optional) - Include call-to-action buttons like 'Click to Pay'.",
        "Step 12: Save the Template - Click 'Save' to finish creating the template.",
        "Step 13: Wait for Approval - Initially status will be 'Pending'. Wait until it’s 'Approved'.",
        "Step 14: Send the Template - Fill recipient number (with +91) and click 'Send'.",
        "Step 15: Confirmation - A popup confirms successful sending and message shows on WhatsApp."
      ],
      videoUrl: "",
    },
    {
      title: "What are the different template categories, and when should you use each?",
      steps: [
        "1. Marketing: Used for sending promotional, advertising, or sales-related messages.",
        "2. Utility: Meant for sharing important updates, reminders, or information.",
        "3. Authentication: Used exclusively for sending one-time passwords (OTPs) and authentication codes."
      ],
    },
    {
      title: "What are Body Variables in a Template?",
      steps: [
        "When sending templates, not all content remains the same for every user. Variables allow you to customize parts of the message, like names, dates, or order numbers, without changing the overall template structure."
      ],
    },
    {
      title: "How do body variables work?",
      steps: [
        "While creating the message template, you can insert variables using numbered placeholders like {{1}}, {{2}}, etc., or you can click on the “Add Variables” button. These placeholders will later be replaced with actual values when the message is sent."
      ],
    },
    {
      title: "Example of a body variable?",
      steps: [
        "Suppose you're creating a reminder template with the line: 'Dear {{1}}, this is a reminder for your appointment on {{2}}.'",
        "Here, {{1}} could be the user’s name, and {{2}} could be the appointment date.",
        "The final message would be: 'Dear John, this is a reminder for your appointment on 25th April.'"
      ],
    },
    {
      title: "What are the different types of buttons available in a template?",
      steps: [
        "1. Custom: Create up to 10 quick reply buttons.",
        "2. Visit Website: Add up to 2 buttons that redirect users to a webpage.",
        "3. Call Phone Number: Add 1 button that lets users directly call a provided number.",
        "4. Offer Code: Add 1 button to share promotional codes or coupons."
      ],
    },
  ];

  // Function to check if the search query matches the topic or its synonyms
  const matchSearch = (title: string, synonyms: string[]) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      title.toLowerCase().includes(searchLower) ||
      synonyms.some((synonym) => synonym.toLowerCase().includes(searchLower))
    );
  };

  const filteredTopics = helpTopics.filter((topic) =>
    matchSearch(topic.title, topicSynonyms[topic.title] || [])
  );

  return (
    <div>
      <IconButton onClick={handleToggle}>
        <HelpOutlineIcon fontSize="medium" />
      </IconButton>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleToggle}
        sx={{ zIndex: 1400 }}
      >
        <Box sx={{ width: 320, p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Help Center</Typography>
            <IconButton onClick={handleToggle}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            variant="outlined"
            size="small"
            placeholder="Search topics..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
          />

          <Divider />

          <List>
            {filteredTopics.map((topic, index) => (
              <div key={index}>
                <ListItem button onClick={() => toggleExpand(topic.title)}>
                  <ListItemText primary={topic.title} />
                  {expandedTopic === topic.title ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItem>
                <Collapse in={expandedTopic === topic.title} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 4, pb: 2 }}>
                    {topic.steps.map((step, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                        {step}
                      </Typography>
                    ))}

                    {/* Video below each help topic */}
                    {topic.videoUrl && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Watch this tutorial:</Typography>
                        <iframe
                          width="100%"
                          height="215"
                          src={topic.videoUrl}
                          frameBorder="0"
                          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </div>
            ))}
          </List>
        </Box>
      </Drawer>
    </div>
  );
};

export default HelpSidebar;
